import React from "react";
import * as axios from "axios";
import {loadGoogleMaps} from "../../lib/googleMaps";
import "./styles.css";

const DEFAULT_CENTER = {lat: 46.5552427, lng: 23.8356132};

// A little blue "car" badge — a Material car glyph plus the car's number
// (the digits in its plate, e.g. "CJ07APP" -> "07") baked directly into the
// SVG, rather than layered on via Marker.label — that sidesteps having to
// reverse-engineer Google's default anchor/labelOrigin for a custom icon;
// this way the marker's own anchor is the only positioning math needed.
const vehicleMarkerIconSvg = (carNumber) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="30" viewBox="0 0 56 30">` +
    `<rect x="1" y="1" width="54" height="28" rx="14" fill="#1a73e8" stroke="#ffffff" stroke-width="2"/>` +
    `<g transform="translate(6,8) scale(0.6)">` +
    `<path fill="#ffffff" d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16C5.67 16 5 15.33 5 14.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>` +
    `</g>` +
    `<text x="38" y="20" text-anchor="middle" font-family="Arial, sans-serif" font-size="13" font-weight="700" fill="#ffffff">${carNumber}</text>` +
    `</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const timeDiff = (dateFuture, dateNow) => {
  let diffInMilliSeconds = Math.abs(dateFuture - dateNow) / 1000;

  // calculate days
  const days = Math.floor(diffInMilliSeconds / 86400);
  diffInMilliSeconds -= days * 86400;

  // calculate hours
  const hours = Math.floor(diffInMilliSeconds / 3600) % 24;
  diffInMilliSeconds -= hours * 3600;

  // calculate minutes
  const minutes = Math.floor(diffInMilliSeconds / 60) % 60;
  diffInMilliSeconds -= minutes * 60;

  const seconds = Math.floor(diffInMilliSeconds) % 60;

  let difference = '';
  if (days > 0) {
    difference += (days === 1) ? `o zi` : `${days} zile`;
    return 'invalid';
  }

  if (hours > 0) {
    difference += (hours === 0 || hours === 1) ? `o oră` : `${hours} ore`;
    return 'mai multe de ' + difference;
  }

  if (minutes > 0) {
    difference += (minutes === 1) ? `un minut`: `${minutes} minute`;
    difference += (seconds > 0) ? ` și ` : '';
  }

  if (seconds > 0) {
    difference += (seconds === 1) ? `o secundă`: `${seconds} secunde`;
  }

  if (difference === '') {
    difference = 'acum';
  }
  return difference;
}

// How long ago the vehicle-positions response was actually refreshed from
// TrackGPS (cached_at, see trackgps.ListVehiclePositions on the backend) —
// distinct from timeDiff above, which is about a deliverer's own last GPS
// fix. No cutoff to 'invalid' here: a stuck TrackGPS refresh should read as
// stale, not disappear.
const formatCacheAge = (cachedAt, now) => {
  if (!cachedAt) {
    return null;
  }
  const seconds = Math.max(0, Math.round((now - new Date(cachedAt)) / 1000));
  if (seconds < 5) {
    return 'acum';
  }
  if (seconds < 60) {
    return `acum ${seconds} secunde`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `acum ${minutes} ${minutes === 1 ? 'minut' : 'minute'}`;
  }
  const hours = Math.round(minutes / 60);
  return `acum ${hours} ${hours === 1 ? 'oră' : 'ore'}`;
};

export default class Map extends React.Component {
  state = {
    deliverers: [],
    vehicles: []
  };
  interval;
  markers = {};
  vehicleMarkers = {};

  componentDidMount() {
    this.getDeliverers();
    this.getVehiclePositions();
    this.interval = setInterval(() => {
      this.getDeliverers();
      this.getVehiclePositions();
    }, 5000);
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  componentDidUpdate() {
    this.renderMarkers();
    this.renderVehicleMarkers();
  }

  getDeliverers = async () => {
    // const baseUrl = 'http://localhost:8080/';
    const baseUrl = 'https://api.appetito.ro/';
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/active-deliverers`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    this.setState({deliverers: response.data});
  };

  // TrackGPS positions for this city's own cars (see Mașini) — a distinct
  // layer from the deliverer-app markers above, since it's a different data
  // source (vehicle GPS hardware vs the deliverer's phone) and can be
  // present/absent independently. The backend caches this for 30s (TrackGPS
  // itself enforces a hard 1-request/30s rate limit), so polling every 5s
  // here is harmless — most calls just get the same cached data back.
  getVehiclePositions = async () => {
    const baseUrl = 'https://api.appetito.ro/';
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/vehicle-positions`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    this.setState({vehicles: response.data || []});
  };

  mapContainerRef = (el) => {
    if (!el) {
      this.map = null;
      this.maps = null;
      return;
    }

    loadGoogleMaps().then((maps) => {
      if (!el.isConnected) {
        return;
      }
      this.maps = maps;
      this.map = new maps.Map(el, {center: DEFAULT_CENTER, zoom: 13});
      this.renderMarkers();
      this.renderVehicleMarkers();
    }).catch(() => {
    });
  };

  renderMarkers = () => {
    if (!this.map || !this.maps) {
      return;
    }
    const now = new Date();
    const seenIds = new Set();

    this.state.deliverers.forEach((deliverer) => {
      const lastDate = new Date(deliverer.last_location);
      const label = timeDiff(now, lastDate);
      if (label === 'invalid') {
        return;
      }
      seenIds.add(deliverer.id);
      const position = {lat: deliverer.latitude, lng: deliverer.longitude};
      const content = `<div class="popup-bubble-content"><h3>${deliverer.first_name} ${deliverer.last_name}</h3><h5>${label}</h5></div>`;

      let entry = this.markers[deliverer.id];
      if (!entry) {
        const marker = new this.maps.Marker({position, map: this.map});
        const infoWindow = new this.maps.InfoWindow({content, disableAutoPan: true});
        infoWindow.open({map: this.map, anchor: marker});
        entry = {marker, infoWindow};
        this.markers[deliverer.id] = entry;
      } else {
        entry.marker.setPosition(position);
        entry.infoWindow.setContent(content);
      }
    });

    Object.keys(this.markers).forEach((id) => {
      if (!seenIds.has(Number(id))) {
        this.markers[id].marker.setMap(null);
        this.markers[id].infoWindow.close();
        delete this.markers[id];
      }
    });
  };

  // Blue markers for TrackGPS vehicle positions — a separate marker set
  // (keyed by car_id, not deliverer id) so this layer never collides with
  // or overwrites the red deliverer-app markers above. Each pin is labeled
  // with the car's number (the digits in its plate, e.g. "CJ07APP" -> "07")
  // and stays collapsed until clicked — with 10+ cars on screen at once,
  // every InfoWindow auto-open at once (the deliverer layer's behavior,
  // above) buried the map under permanently-open bubbles.
  renderVehicleMarkers = () => {
    if (!this.map || !this.maps) {
      return;
    }
    const seenIds = new Set();
    const now = new Date();

    (this.state.vehicles || []).forEach((vehicle) => {
      seenIds.add(vehicle.car_id);
      const position = {lat: vehicle.latitude, lng: vehicle.longitude};
      const carNumber = (vehicle.plate_number || '').match(/\d+/)?.[0] || '';
      const driverLine = vehicle.deliverer_name ? `<h5>${vehicle.deliverer_name}</h5>` : '';
      const statusLine = `${vehicle.ignition_state === 'ON' ? 'Pornită' : 'Oprită'} · ${Math.round(vehicle.speed)} km/h`;
      const cacheAge = formatCacheAge(vehicle.cached_at, now);
      const cacheLine = cacheAge ? `<h5>Actualizat ${cacheAge}</h5>` : '';
      const content = `<div class="popup-bubble-content"><h3>${vehicle.plate_number} — ${vehicle.make} ${vehicle.model}</h3>${driverLine}<h5>${statusLine}</h5>${cacheLine}</div>`;

      let entry = this.vehicleMarkers[vehicle.car_id];
      if (!entry) {
        const marker = new this.maps.Marker({
          position,
          map: this.map,
          icon: {
            url: vehicleMarkerIconSvg(carNumber),
            scaledSize: new this.maps.Size(56, 30),
            anchor: new this.maps.Point(28, 15),
          },
        });
        const infoWindow = new this.maps.InfoWindow({content, disableAutoPan: true});
        marker.addListener('click', () => infoWindow.open({map: this.map, anchor: marker}));
        entry = {marker, infoWindow};
        this.vehicleMarkers[vehicle.car_id] = entry;
      } else {
        entry.marker.setPosition(position);
        entry.infoWindow.setContent(content);
      }
    });

    Object.keys(this.vehicleMarkers).forEach((id) => {
      if (!seenIds.has(Number(id))) {
        this.vehicleMarkers[id].marker.setMap(null);
        this.vehicleMarkers[id].infoWindow.close();
        delete this.vehicleMarkers[id];
      }
    });
  };

  render() {
    return (
      <div style={{display: 'flex', flex: 1}}>
        <div className="map-container" ref={this.mapContainerRef}/>
      </div>
    )
  }
}
